/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	forwardRef,
	Fragment,
	type MouseEvent,
	type ReactNode,
	type RefObject,
	useMemo,
} from 'react';

import { css, jsx } from '@compiled/react';
import { type Cell, flexRender, type Table, type Row as TanstackRow } from '@tanstack/react-table';
import type { VirtualItem } from '@tanstack/react-virtual';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ExpandIcon } from '../ui/primitives/expand-icon';

import { type CharlieTableProps } from './charlie-table.types';
import { TBody } from './components/body';
import { PragmaticRow, type PragmaticRowProps } from './components/pragmatic-row';
import { Row } from './components/row';
import { RowCell } from './components/row-cell';
import type { ColumnVirtualizer } from './hooks/virtualization/use-charlie-column-virtualization';
import { useCharlieRowVirtualization } from './hooks/virtualization/use-charlie-row-virtualization';
import {
	getFirstNonReservedColumnIndex,
	isFirstNonReservedColumn,
	isReservedColumn,
} from './utils/reserved-column-utils';

export type CharlieTableBodyProps<T extends object> = {
	tableBodyRef?: RefObject<HTMLTableSectionElement>;
	table: Table<T>;
	renderOptions?: CharlieTableProps<T>['renderOptions'];
	rowSpacing?: CharlieTableProps<T>['rowSpacing'];
	onRowClick?: CharlieTableProps<T>['onRowClick'];
	tableType?: CharlieTableProps<T>['tableType'];
	renderChildComponent?: CharlieTableProps<T>['renderChildComponent'];
	enableRowSelection?: CharlieTableProps<T>['enableRowSelection'];
	rowCellXcss?: CharlieTableProps<T>['rowCellXcss'];
	interactionName?: CharlieTableProps<T>['interactionName'];
	labels: CharlieTableProps<T>['labels'];
	analyticsContext?: CharlieTableProps<T>['analyticsContext'];
	enableKeyboardNavigation?: CharlieTableProps<T>['enableKeyboardNavigation'];
	tableNavigationControllers?: (event: React.KeyboardEvent<HTMLTableCellElement>) => void;
	containerRef: RefObject<HTMLDivElement>;
	enableRowVirtualization: boolean;
	rowVirtualizerRef?: NonNullable<CharlieTableProps<T>['refs']>['rowVirtualizerRef'];
	rowVirtualizerOptions?: CharlieTableProps<T>['rowVirtualizerOptions'];
	infiniteScrollRef?: RefObject<HTMLTableRowElement>;
	hasNext?: CharlieTableProps<T>['hasNext'];
	responsiveColumnSizing?: CharlieTableProps<T>['responsiveColumnSizing'];
	columnVirtualizer?: ColumnVirtualizer;
	enableRowReorder?: boolean;
	onDraggingRowEnd?: CharlieTableProps<T>['onDraggingRowEnd'];
	componentPropOverrides?: CharlieTableProps<T>['componentPropOverrides'];
};

const SubRowExpandableStyle = css({
	left: token('space.0'),
	whiteSpace: 'nowrap',
	borderColor: token('color.border'),
	borderBottomWidth: token('border.width'),
	borderInlineWidth: token('border.width'),
	borderBottomStyle: 'solid',
	borderInlineStyle: 'solid',
	zIndex: 100,
	position: 'var(--col-group-position)',
	width: '100%',
	height: '100%',
	paddingBlock: token('space.0'),
});

const groupedRowBackgroundStyles = cssMap({
	success: {
		backgroundColor: token('color.background.success'),
	},
	danger: {
		backgroundColor: token('color.background.danger'),
	},
	information: {
		backgroundColor: token('color.background.information'),
	},
	discovery: {
		backgroundColor: token('color.background.discovery'),
	},
	warning: {
		backgroundColor: token('color.background.warning'),
	},
	selected: {
		backgroundColor: token('color.background.selected'),
	},
	disabled: {
		// Using 'input.hovered' to avoid any alpha transparency issues with the background color (which affects pinning, for example). Other Neutral100's are too dark in dark mode.
		backgroundColor: token('color.background.input.hovered'),
	},
});

const calculateRowPadding = <T extends object>(
	row: TanstackRow<T>,
	depthAddition: number,
	renderOptions?: CharlieTableProps<T>['renderOptions'],
) => {
	const depthUnit = renderOptions?.expandableRow?.depthUnit ?? 8;
	const alignNonExpandable = renderOptions?.expandableRow?.alignNonExpandableRows ?? true;

	const basePadding = (row.depth + depthAddition) * depthUnit;
	const expandableWidth = 20;
	const rowPadding = !row.getCanExpand() && alignNonExpandable ? expandableWidth : 0;
	return `${basePadding + rowPadding}px`;
};

const ExpandRowButton = <T extends object>({
	row,
	interactionName,
	analyticsContext,
	labels,
}: {
	row: TanstackRow<T>;
	interactionName?: CharlieTableProps<T>['interactionName'];
	analyticsContext?: CharlieTableProps<T>['analyticsContext'];
	labels?: CharlieTableProps<T>['labels'];
}) => (
	<IconButton
		spacing="compact"
		testId="expand-row-button"
		appearance="subtle"
		interactionName={interactionName}
		analyticsContext={{
			componentName: 'charlieTableExpandRowButton',
			...analyticsContext,
		}}
		aria-expanded={row.getIsExpanded()}
		label={typeof labels?.expandRow === 'function' ? labels.expandRow(row) : labels?.expandRow}
		icon={(iconProps) => <ExpandIcon isExpanded={row.getIsExpanded() ?? false} {...iconProps} />}
		onClick={(e) => {
			e.stopPropagation();
			row.toggleExpanded();
		}}
	/>
);

const styles = cssMap({
	reservedColumnPadding: {
		paddingInlineStart: token('space.100'),
	},
	firstColumnBorder: {
		borderInlineStart: `${token('border.width')} solid ${token('color.border')}`,
	},
	lastColumnBorder: {
		borderInlineEnd: `${token('border.width')} solid ${token('color.border')}`,
	},
	subRowExpandableContainer: {
		height: '100%',
	},

	RowCursorPointer: {
		cursor: 'pointer',
	},

	SubRowButtonWrapperStyle: {
		marginInlineEnd: token('space.050'),
	},
	SubRowButtonFirstIndex: {
		marginInlineStart: token('space.negative.100'),
	},
	tableBody: {
		isolation: 'isolate',
	},
	virtualRow: {
		position: 'absolute',
	},
	columnResponsive: {
		flexGrow: 1,
	},

	SubRowExpandableStyleDefaultRowSize: {
		minHeight: '45px',
	},
	SubRowExpandableStyleCompactRowSize: {
		minHeight: '36px',
	},
});

export const CharlieTableBody = <
	T extends object & { overrideRender?: (rowData: T) => ReactNode },
>({
	table,
	tableBodyRef,
	renderOptions,
	rowSpacing,
	onRowClick,
	tableType,
	renderChildComponent,
	enableRowSelection,
	rowCellXcss,
	interactionName,
	labels,
	enableKeyboardNavigation,
	tableNavigationControllers,
	analyticsContext,
	containerRef,
	enableRowVirtualization,
	rowVirtualizerRef,
	rowVirtualizerOptions,
	infiniteScrollRef,
	hasNext,
	responsiveColumnSizing,
	columnVirtualizer,
	enableRowReorder = false,
	onDraggingRowEnd: _onDraggingRowEnd,
	componentPropOverrides,
}: CharlieTableBodyProps<T>) => {
	const rowVirtualizer = useCharlieRowVirtualization({
		enableRowVirtualization,
		table,
		tableContainerRef: containerRef,
		rowSpacing,
		rowVirtualizerRef,
		rowVirtualizerOptions,
	});
	const { rows: rowsFromModel } = table.getRowModel();
	const rows = rowVirtualizer ? rowVirtualizer.getVirtualItems() : rowsFromModel;

	const RowComponent = useMemo(() => {
		if (enableRowReorder) {
			return forwardRef<HTMLTableRowElement, PragmaticRowProps<T>>((props, ref) => {
				// Filter out pragmatic-specific props for the fallback
				return <PragmaticRow {...props} ref={ref} />;
			});
		} else {
			// Filter out DND-specific props for regular Row component
			return Row;
		}
	}, [enableRowReorder]);

	const { virtualColumns, virtualPaddingLeft, virtualPaddingRight } = columnVirtualizer ?? {};

	return (
		<TBody
			ref={tableBodyRef}
			xcss={styles.tableBody}
			style={{
				//tells scrollbar how big the table is
				height: rowVirtualizer ? `${rowVirtualizer.getTotalSize()}px` : undefined,
			}}
		>
			{rows ? (
				rows.map((rowOrVirtualRow, index) => {
					const virtualRow = rowVirtualizer ? (rowOrVirtualRow as VirtualItem) : undefined;
					const row = rowVirtualizer
						? rowsFromModel[rowOrVirtualRow.index]
						: (rowOrVirtualRow as TanstackRow<T>);

					const visibleCells = row.getVisibleCells();

					// Get background color for grouped rows if provided
					const groupedRowBackground = row.getIsGrouped()
						? renderOptions?.groupedRowBackground?.({
								rowData: row.original,
								state: table.getState(),
							})
						: undefined;
					return (
						<Fragment key={row.id}>
							{row.original?.overrideRender ||
							(row.getIsGrouped() && renderOptions?.groupingRow) ? (
								<Fragment>
									<RowComponent
										row={row}
										index={virtualRow?.index ?? index}
										{...(enableRowReorder && { enableRowReorder })}
										data-index={rowOrVirtualRow.index}
										style={{
											// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
											...(virtualRow
												? {
														transform: `translateY(${virtualRow.start}px)`, //this should always be a `style` as it changes on scroll
													}
												: undefined),
										}}
										ref={(node: HTMLTableRowElement | null) =>
											rowVirtualizer ? rowVirtualizer.measureElement(node) : undefined
										} //measure dynamic row height
										spacing={rowSpacing}
										onClick={(e: MouseEvent<HTMLElement>) => {
											// @ts-expect-error Prevent row click if a link was clicked
											if (e.target.closest('a')) {
												return;
											}
											onRowClick?.(row);
										}}
										{...(componentPropOverrides?.row && fg('charly-table-with-row-table-handler')
											? { ...componentPropOverrides.row({ row }) }
											: {})}
										xcss={cx(
											onRowClick && styles.RowCursorPointer,
											virtualRow && styles.virtualRow,
										)}
									>
										{/* Not using TD here in order to avoid some overrides we've added */}
										<td
											// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop

											colSpan={row.getVisibleCells().length}
											css={[
												SubRowExpandableStyle,
												// must set the minHeight on the <td /> because Safari does not fill height of min-height <tr />
												rowSpacing === 'compact'
													? styles.SubRowExpandableStyleCompactRowSize
													: styles.SubRowExpandableStyleDefaultRowSize,
											]}
										>
											<Flex
												alignItems="center"
												xcss={cx(
													styles.subRowExpandableContainer,
													groupedRowBackground && groupedRowBackgroundStyles[groupedRowBackground],
												)}
											>
												{(renderChildComponent || renderOptions?.groupingRow) &&
													row.getCanExpand() && (
														<Box
															// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
															style={{
																paddingInlineStart: calculateRowPadding(row, 1, renderOptions),
															}}
															xcss={cx(styles.SubRowButtonWrapperStyle)}
														>
															<ExpandRowButton
																row={row}
																interactionName={interactionName}
																analyticsContext={analyticsContext}
																labels={labels}
															/>
														</Box>
													)}
												{row.original.overrideRender && row.original.overrideRender(row.original)}
												{renderOptions?.groupingRow &&
													renderOptions?.groupingRow(row.original, table.getState())}
											</Flex>
										</td>
									</RowComponent>
								</Fragment>
							) : (
								<RowComponent
									row={row}
									index={virtualRow?.index ?? index}
									{...(enableRowReorder && { enableRowReorder })}
									data-index={virtualRow?.index}
									key={row.id}
									ref={(node: HTMLTableRowElement | null) =>
										rowVirtualizer ? rowVirtualizer.measureElement(node) : undefined
									} //measure dynamic row height
									spacing={rowSpacing}
									onClick={(e: MouseEvent<HTMLElement>) => {
										// @ts-expect-error Prevent row click if a link was clicked
										if (e.target.closest('a')) {
											return;
										}
										onRowClick?.(row);
									}}
									{...(componentPropOverrides?.row && fg('charly-table-with-row-table-handler')
										? { ...componentPropOverrides.row({ row }) }
										: {})}
									xcss={cx(onRowClick && styles.RowCursorPointer, virtualRow && styles.virtualRow)}
									style={{
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
										...(virtualRow
											? {
													transform: `translateY(${virtualRow.start}px)`, //this should always be a `style` as it changes on scroll
												}
											: undefined),
									}}
								>
									{virtualPaddingLeft ? (
										//eslint-disable-next-line  @atlaskit/ui-styling-standard/enforce-style-prop
										<td style={{ display: 'flex', width: virtualPaddingLeft }} />
									) : null}
									{(virtualColumns ?? visibleCells).map((cellOrVirtualCell, index) => {
										// the first column has some weird overrides that we need to account for
										const cell = virtualColumns
											? visibleCells[(cellOrVirtualCell as VirtualItem).index]
											: (cellOrVirtualCell as Cell<T, unknown>);
										const depthAddition = index === 0 ? 1 : 0;
										const cellBackground = renderOptions?.bodyCellBackground?.({
											cell: cell,
											state: table.getState(),
										});
										const allColumns = table.getAllLeafColumns();
										const isFirstNonReserved = isFirstNonReservedColumn(allColumns, index);
										const firstNonReservedColumnIndex = getFirstNonReservedColumnIndex(allColumns);
										const isResponsiveColumn = responsiveColumnSizing?.(cell.column);
										return (
											<RowCell
												isOverridingResponsiveColumnSizing={responsiveColumnSizing != null}
												tableType={tableType}
												columnId={cell.column.id}
												rowSpacing={rowSpacing}
												cellBackground={cellBackground}
												xcss={cx(
													isReservedColumn(cell.column.id) && styles.reservedColumnPadding,
													isResponsiveColumn && styles.columnResponsive,
													cell.row.getCanExpand() &&
														isFirstNonReserved &&
														styles.reservedColumnPadding,
													index === 0 && styles.firstColumnBorder,
													index === 0 && styles.reservedColumnPadding,
													index === visibleCells.length - 1 && styles.lastColumnBorder,
													rowCellXcss,
												)}
												key={cell.id}
												{...(enableKeyboardNavigation && {
													tabIndex: 0,
													onKeyDown: tableNavigationControllers,
												})}
											>
												<Fragment>
													{((row.depth > 0 && isFirstNonReserved) ||
														(firstNonReservedColumnIndex === index &&
															index === 0 &&
															(!virtualPaddingLeft || isFirstNonReserved))) && (
														<Box
															// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
															style={
																isFirstNonReserved &&
																!row.getParentRow()?.getIsGrouped() &&
																table.getCanSomeRowsExpand()
																	? {
																			paddingInlineStart: calculateRowPadding(
																				row,
																				depthAddition,
																				renderOptions,
																			),
																		}
																	: undefined
															}
															xcss={styles.reservedColumnPadding}
														/>
													)}
													{row.getCanExpand() && isFirstNonReserved && (
														<Box
															xcss={cx(
																styles.SubRowButtonWrapperStyle,
																!enableRowSelection && index === 0 && styles.SubRowButtonFirstIndex,
															)}
														>
															<ExpandRowButton
																row={row}
																interactionName={interactionName}
																analyticsContext={analyticsContext}
																labels={labels}
															/>
														</Box>
													)}
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</Fragment>
											</RowCell>
										);
									})}
									{virtualPaddingRight ? (
										//eslint-disable-next-line  @atlaskit/ui-styling-standard/enforce-style-prop
										<td style={{ display: 'flex', width: virtualPaddingRight }} />
									) : null}
								</RowComponent>
							)}
							{row.getIsExpanded() && renderChildComponent && (
								<Fragment>
									{renderChildComponent({
										rowData: row,
										tableState: table.getState(),
									})}
								</Fragment>
							)}
							{hasNext &&
								!rowVirtualizer &&
								index === Math.floor((table.getRowModel().rows?.length ?? 0) * 0.92) && (
									// if user is 92% through the table, load more rows
									// TODO: This will be deprecated in the future, we will use fetchMoreOnScroll instead
									<tr key={'loadMoreTrigger'} ref={infiniteScrollRef} />
								)}
						</Fragment>
					);
				})
			) : (
				<Fragment></Fragment>
			)}
		</TBody>
	);
};

export const MemoCharlieTableBody = <
	T extends object & { overrideRender?: (rowData: T) => ReactNode },
>(
	props: CharlieTableBodyProps<T>,
) => {
	const {
		table: {
			options: {
				data,
				state: { rowSelection, sorting, expanded, columnOrder, grouping, columnVisibility },
			},
		},
		containerRef,
		rowSpacing,
		renderOptions,
		componentPropOverrides,
	} = props;
	return useMemo(
		() => <CharlieTableBody {...props} />,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			rowSpacing,
			rowSelection,
			sorting,
			expanded,
			columnOrder,
			grouping,
			columnVisibility,
			data,
			containerRef,
			renderOptions,
			componentPropOverrides,
		],
	);
};
